import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SkeletonFiller } from 'components/SkeletonFiller';
import { GoToGivebutterButton } from 'components/account/GoToGivebutterButton';
import { NoCampaigns } from 'components/campaign/NoCampaigns';
import { DynamicField, FormSchemaEntry } from 'components/form/DynamicField';
import { FormCombobox } from 'components/form/FormCombobox';
import { FormRadioCards } from 'components/form/FormRadioCards';
import { FormTextInput } from 'components/form/FormTextInput';
import { WidgetFormProvider, useWidgetFormContext } from 'components/form/WidgetFormContext';
import { configWithDefaults, defaultsFromSchema, sanitizeConfig } from 'lib/element-config';
import { ElementOptions, WidgetType, WixCampaign, WixElementWithForm, createApi } from 'lib/gb-api';
import { twMerge } from 'tailwind-merge';
import { WidgetPreview } from './WidgetPreview';

type Api = ReturnType<typeof createApi>;
type WidgetForms = ElementOptions['forms'];

interface WidgetFormProps {
  api: Api;
  accountAid: string;
  accountId?: number | null;
  editingDisplayId?: string;
  onSaved: (element?: WixElementWithForm) => void;
  onCancel?: () => void;
  onCleared?: () => void;
  compact?: boolean;
  autosave?: boolean;
}

interface FormValues {
  type: WidgetType;
  campaign_id: string;
  name: string;
  config: Record<string, unknown>;
}

const WIDGET_TYPE_OPTIONS: { value: WidgetType; label: string; description: string }[] = [
  {
    value: 'button',
    label: 'Donation button',
    description: 'Popup donation button for any part of your site.',
  },
  {
    value: 'giving-form',
    label: 'Donation form',
    description: 'Embedded donation form directly on your webpage.',
  },
  {
    value: 'signup-form',
    label: 'Signup form',
    description: 'Grow your audience with embeddable signup forms.',
  },
];

const getCampaignId = (values: FormValues) =>
  values.campaign_id === '' ? undefined : Number(values.campaign_id);

const buildUpdateInput = (values: FormValues): Parameters<Api['updateElement']>[1] => ({
  name: values.name || undefined,
  campaign_id: getCampaignId(values),
  config: sanitizeConfig(values.type, values.config),
});

const buildDefaultValues = (
  element: WixElementWithForm | undefined,
  forms: WidgetForms,
  campaigns: WixCampaign[],
): FormValues => {
  const type: WidgetType = element?.type ?? 'button';
  const schema = forms[type];

  if (element) {
    return {
      type,
      campaign_id: element.campaign_id != null ? String(element.campaign_id) : '',
      name: element.name ?? '',
      config: schema ? configWithDefaults(type, schema, element.config) : (element.config ?? {}),
    };
  }

  return {
    type,
    campaign_id: campaigns.length > 0 ? String(campaigns[0].id) : '',
    name: '',
    config: schema ? defaultsFromSchema(type, schema) : {},
  };
};

interface WidgetFormFieldsProps {
  api: Api;
  accountAid: string;
  accountId?: number | null;
  element?: WixElementWithForm;
  forms: WidgetForms;
  campaigns: WixCampaign[];
  onSaved: (element?: WixElementWithForm) => void;
  onCancel?: () => void;
  autosave: boolean;
}

const WidgetForm = ({
  api,
  accountAid,
  accountId,
  editingDisplayId,
  onSaved,
  onCancel,
  onCleared,
  autosave = false,
}: WidgetFormProps) => {
  const optionsQuery = useQuery({
    queryKey: ['wix', 'element-options'],
    queryFn: api.getElementOptions,
  });

  const campaignsQuery = useQuery({
    queryKey: ['wix', 'campaigns'],
    queryFn: api.listCampaigns,
  });

  const existingQuery = useQuery({
    queryKey: ['wix', 'element', editingDisplayId],
    queryFn: () => api.getElement(editingDisplayId!),
    enabled: !!editingDisplayId,
  });

  const loadError = optionsQuery.error ?? campaignsQuery.error ?? existingQuery.error;

  if (loadError) {
    return (
      <WidgetFormError
        error={loadError}
        onCancel={onCancel}
        onRetry={() => {
          if (optionsQuery.error) optionsQuery.refetch();
          if (campaignsQuery.error) campaignsQuery.refetch();
          if (existingQuery.error) existingQuery.refetch();
        }}
      />
    );
  }

  const element = existingQuery.data;
  const forms = optionsQuery.data?.forms;
  const campaigns = campaignsQuery.data;

  const notFound =
    (existingQuery.error as { response?: { status?: number } })?.response?.status === 404;

  if (element?.deleted_at || notFound) {
    return <WidgetDeletedDisplay onCleared={onCleared} />;
  }

  if (!forms || !campaigns || (editingDisplayId && !element)) {
    return <SkeletonFiller />;
  }

  return (
    <WidgetFormFields
      key={element?.display_id ?? 'new'}
      api={api}
      accountAid={accountAid}
      accountId={accountId}
      element={element}
      forms={forms}
      campaigns={campaigns}
      onSaved={onSaved}
      onCancel={onCancel}
      autosave={autosave}
    />
  );
};

const WidgetFormFields = ({
  api,
  accountAid,
  accountId,
  element,
  forms,
  campaigns,
  onSaved,
  onCancel,
  autosave,
}: WidgetFormFieldsProps) => {
  const { compact } = useWidgetFormContext();
  const queryClient = useQueryClient();

  const { register, handleSubmit, control, watch, setValue, getValues } = useForm<FormValues>({
    defaultValues: buildDefaultValues(element, forms, campaigns),
  });

  const selectedType = watch('type');
  const selectedCampaignId = watch('campaign_id');
  const watchedConfig = watch('config');

  const formSchema = forms[selectedType];

  const seededTypeRef = useRef<WidgetType>(selectedType);

  useEffect(() => {
    if (element || !formSchema || seededTypeRef.current === selectedType) {
      return;
    }
    seededTypeRef.current = selectedType;
    setValue('config', defaultsFromSchema(selectedType, formSchema));
  }, [element, selectedType, formSchema, setValue]);

  const availableCampaigns = useMemo(() => {
    const saved = element?.campaign;
    if (!saved || campaigns.some(c => c.id === saved.id)) {
      return campaigns;
    }
    return [{ ...saved, type: '' }, ...campaigns];
  }, [campaigns, element?.campaign]);

  const selectedCampaign = useMemo(
    () =>
      selectedCampaignId === ''
        ? undefined
        : availableCampaigns.find(c => c.id === Number(selectedCampaignId)),
    [availableCampaigns, selectedCampaignId],
  );

  const createMutation = useMutation({
    mutationFn: api.createElement,
    onSuccess: onSaved,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      displayId,
      input,
    }: {
      displayId: string;
      input: Parameters<Api['updateElement']>[1];
    }) => api.updateElement(displayId, input),
    onSuccess: updated => {
      queryClient.setQueryData(['wix', 'element', updated.display_id], updated);
      onSaved(updated);
    },
  });

  const onSubmit = (values: FormValues) => {
    if (element) {
      updateMutation.mutate({
        displayId: element.display_id,
        input: buildUpdateInput(values),
      });
    } else {
      createMutation.mutate({
        type: values.type,
        ...buildUpdateInput(values),
      });
    }
  };

  const [autosaveSnapshot, setAutosaveSnapshot] = useState<string | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const lastAttemptedRef = useRef<string | null>(null);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!autosave) {
      return;
    }
    setAutosaveSnapshot(JSON.stringify(getValues()));
    const subscription = watch(values => setAutosaveSnapshot(JSON.stringify(values)));
    return () => subscription.unsubscribe();
  }, [autosave, watch, getValues]);

  useEffect(() => {
    if (!autosave || !element || element?.deleted_at || autosaveSnapshot === null) {
      return;
    }
    if (lastSavedRef.current === null) {
      lastSavedRef.current = autosaveSnapshot;
      return;
    }
    if (
      autosaveSnapshot === lastSavedRef.current ||
      autosaveSnapshot === lastAttemptedRef.current ||
      isSubmitting
    ) {
      return;
    }
    const timer = setTimeout(() => {
      const snapshot = autosaveSnapshot;
      lastAttemptedRef.current = snapshot;
      handleSubmit(values => {
        updateMutation.mutate(
          {
            displayId: element.display_id,
            input: buildUpdateInput(values),
          },
          {
            onSuccess: () => {
              lastSavedRef.current = snapshot;
              lastAttemptedRef.current = null;
            },
          },
        );
      })();
    }, 700);
    return () => clearTimeout(timer);
  }, [autosaveSnapshot, autosave, element, isSubmitting, handleSubmit, updateMutation]);

  const requiresCampaign = selectedType !== 'signup-form';
  const noCampaigns = campaigns.length === 0;
  const blockedByNoCampaigns = requiresCampaign && noCampaigns;
  const submitError = (createMutation.error || updateMutation.error) as {
    response?: { data?: { message?: string } };
  } | null;

  const groupedFields = useMemo(() => {
    if (!formSchema) return [];
    const isButtonStatic = selectedType === 'button' && watchedConfig?.type === 'static';
    const groups: Record<string, [string, FormSchemaEntry][]> = {};
    for (const [key, entry] of Object.entries(formSchema)) {
      if (key === 'campaign') continue;
      if (key === 'position' && isButtonStatic) continue;
      const group = entry.group ?? 'General';
      groups[group] = groups[group] ?? [];
      groups[group].push([key, entry]);
    }
    return Object.entries(groups);
  }, [formSchema, selectedType, watchedConfig?.type]);

  const containerClass = compact
    ? 'flex flex-col gap-4 p-4'
    : 'mx-auto flex max-w-5xl flex-col gap-6 p-8';
  const fieldGridClass = compact
    ? 'grid grid-cols-1 gap-4'
    : 'grid grid-cols-1 gap-4 md:grid-cols-2';
  const bodyLayoutClass = compact
    ? 'flex flex-col gap-4'
    : 'grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={containerClass} noValidate>
      <header
        className={compact ? 'flex flex-col gap-2' : 'flex items-center justify-between gap-4'}
      >
        {!compact && (
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {element ? 'Edit widget' : 'Create widget'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Configure how this widget looks on your Wix site.
            </p>
          </div>
        )}
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              color="neutral"
              onClick={onCancel}
              size={compact ? 'sm' : undefined}
            >
              {compact ? 'Back' : 'Cancel'}
            </Button>
          )}
          {autosave ? (
            <span className="text-xs text-gray-400">
              {isSubmitting ? 'Saving…' : 'Changes save automatically'}
            </span>
          ) : (
            <Button
              type="submit"
              variant="solid"
              color="primary"
              disabled={isSubmitting || blockedByNoCampaigns}
              size={compact ? 'sm' : undefined}
            >
              {isSubmitting ? 'Saving…' : element ? 'Save changes' : 'Create widget'}
            </Button>
          )}
        </div>
      </header>

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {submitError?.response?.data?.message ?? 'Could not save widget'}
        </div>
      )}

      <div className={bodyLayoutClass}>
        <div className={compact ? 'flex flex-col gap-4' : 'flex flex-col gap-6'}>
          {!element && (
            <FormRadioCards
              name="type"
              control={control}
              label="Widget type"
              options={WIDGET_TYPE_OPTIONS}
            />
          )}

          <FormTextInput
            name="name"
            control={control}
            label="Widget name"
            placeholder={`My ${selectedType}`}
          />
          <div className={twMerge(compact ? '-mx-4 border-b' : 'hidden')} />

          {requiresCampaign &&
            (noCampaigns ? (
              <NoCampaigns accountId={accountId ?? null} compact={compact} />
            ) : (
              <>
                <FormCombobox
                  name="campaign_id"
                  control={control}
                  label="Campaign"
                  placeholder="Select a campaign…"
                  searchPlaceholder="Search campaigns"
                  resource="campaign"
                  options={availableCampaigns.map(c => ({
                    value: String(c.id),
                    label: c.title,
                    searchTerms: [c.code, String(c.id)],
                  }))}
                />
                <div className={twMerge(compact ? '-mx-4 border-b' : 'hidden')} />
              </>
            ))}

          {compact ? (
            <>
              {groupedFields.map(([groupName, fields]) => (
                <div className={fieldGridClass} key={groupName}>
                  {fields.map(([fieldKey, entry]) => (
                    <Fragment key={fieldKey}>
                      <DynamicField fieldKey={fieldKey} schema={entry} control={control} />
                      <div className="-mx-4 border-b" />
                    </Fragment>
                  ))}
                </div>
              ))}
            </>
          ) : (
            <>
              {groupedFields.map(([groupName, fields]) => (
                <section
                  key={groupName}
                  className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3"
                >
                  <h2 className="text-xs font-semibold tracking-wide text-gray-700 uppercase">
                    {groupName}
                  </h2>
                  <div className={fieldGridClass}>
                    {fields.map(([fieldKey, entry]) => (
                      <DynamicField
                        key={fieldKey}
                        fieldKey={fieldKey}
                        schema={entry}
                        control={control}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </>
          )}
        </div>

        {!compact && (
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Preview</h3>
            <WidgetPreview
              type={selectedType}
              accountAid={accountAid}
              campaignCode={selectedCampaign?.code}
              config={sanitizeConfig(selectedType, watchedConfig ?? {})}
              savedDisplayId={element?.display_id}
            />
            <p className="text-xs text-gray-500">
              {element
                ? 'Saved changes appear here after save.'
                : 'Configure fields to see a live preview.'}
            </p>
          </div>
        )}
      </div>

      <GoToGivebutterButton accountId={accountId?.toString()} />

      <input type="hidden" {...register('type')} />
    </form>
  );
};

const WidgetFormError = ({
  error,
  onRetry,
  onCancel,
}: {
  error: { response?: { status?: number; data?: { message?: string } } };
  onRetry: () => void;
  onCancel?: () => void;
}) => {
  const { compact } = useWidgetFormContext();

  return (
    <div
      className={compact ? 'flex flex-col gap-3 p-4' : 'mx-auto flex max-w-2xl flex-col gap-3 p-8'}
    >
      <h1
        className={compact ? 'font-semibold text-gray-900' : 'text-xl font-semibold text-gray-900'}
      >
        Couldn't load the widget editor
      </h1>
      <p className="text-sm text-gray-600">
        {error?.response?.status ? `HTTP ${error.response.status} — ` : ''}
        {error?.response?.data?.message ?? 'Unknown error loading widget data.'}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="solid" color="primary" size={compact ? 'sm' : undefined} onClick={onRetry}>
          Try again
        </Button>
        {onCancel && (
          <Button
            variant="outline"
            color="neutral"
            size={compact ? 'sm' : undefined}
            onClick={onCancel}
          >
            Back to widgets
          </Button>
        )}
      </div>
    </div>
  );
};

const WidgetDeletedDisplay = ({ onCleared }: { onCleared?: () => void }) => {
  return (
    <div className="flex flex-col gap-4 p-4">
      <FontAwesomeIcon icon={faClose} size="2x" className="text-error-700" />
      <div className="flex flex-col gap-1">
        <p>Widget missing or deleted</p>
        <p className="text-sm text-gray-500">
          {onCleared
            ? 'Clear this element, or remove it from your page.'
            : 'Please remove this widget from your page.'}
        </p>
      </div>
      {onCleared && (
        <Button variant="outline" color="neutral" size="sm" onClick={onCleared}>
          Set up a new widget
        </Button>
      )}
    </div>
  );
};

const WidgetFormWrapper = ({ compact = false, ...rest }: WidgetFormProps) => {
  return (
    <WidgetFormProvider value={{ compact }}>
      <WidgetForm {...rest} />
    </WidgetFormProvider>
  );
};

export { WidgetFormWrapper as WidgetForm };
